<template>
  <div class="localization-container">
    <Sidebar />
    <div class="main-content">
      <div class="header-controls">

        
        <button @click="exportLocales" class="export-button" :disabled="isExporting">
          <span v-if="!isExporting">Export Locales</span>
          <span v-else>Exporting...</span>
        </button>
      </div>

      <div class="search-container">
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="Search translations..." 
          class="search-input"
        />
      </div>

      <div class="table-container">
        <table class="translations-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Language</th>
              <th>Code</th>
              <th>Last Modified</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in translationsList" :key="item.id">
              <td>{{ item.id }}</td>
              <td>{{ item.name }}</td>
              <td>{{ item.code }}</td>
              <td>{{ formatDate(item.updated_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/plugins/axios';
import Sidebar from './Sidebar.vue';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

export default {
  name: 'LocalizationSelector',
  components: {
    Sidebar
  },
  data() {
    return {
      rawData: [],
      availableLanguages: [],
      selectedLanguage: 'en', // Default language
      isExporting: false,
      editingItem: null,
      isSaving: false,
      searchQuery: '',
    };
  },
  computed: {
    translationsList() {
      return this.rawData;
    }
  },
  async created() {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        this.$emit('error', 'No authentication token found');
        return;
      }

      // Fetch available languages first
      const languagesResponse = await axios.get('/admin/languages', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (languagesResponse.data && languagesResponse.data.languages) {
        this.rawData = languagesResponse.data.languages;
        this.availableLanguages = languagesResponse.data.languages.map(lang => ({
          key: lang.code,
          name: lang.name
        }));
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
      this.$emit('error', 'Failed to load languages');
    }
  },
  methods: {
    handleLanguageChange() {
      this.$emit('language-change', this.selectedLanguage);
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    },
    async exportLocales() {
      this.isExporting = true;
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          this.$emit('error', 'No authentication token found');
          return;
        }

        // Create a new PDF document
        const doc = new jsPDF();
        
        // Add title
        doc.setFontSize(20);
        doc.text('Translations Export', 14, 15);
        
        // Add date and language info
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 25);
        doc.text(`Language: ${this.selectedLanguage.toUpperCase()}`, 14, 35);
        
        // Prepare table data
        const tableData = this.translationsList.map(item => [
          item.id,
          item.name,
          item.code,
          this.formatDate(item.created_at)
        ]);
        
        // Add table
        doc.autoTable({
          startY: 45,
          head: [['ID', 'Text', 'Status', 'Last Modified']],
          body: tableData,
          theme: 'grid',
          headStyles: {
            fillColor: [41, 128, 185],
            textColor: 255,
            fontStyle: 'bold'
          },
          styles: {
            fontSize: 10,
            cellPadding: 5
          },
          columnStyles: {
            0: { cellWidth: 20 }, // ID
            1: { cellWidth: 80 }, // Text
            2: { cellWidth: 30 }, // Status
            3: { cellWidth: 60 }  // Last Modified
          }
        });
        
        // Save the PDF
        doc.save(`translations_${this.selectedLanguage}.pdf`);
        
        this.$emit('success', 'Translations exported successfully');
      } catch (error) {
        console.error('Error exporting translations:', error);
        this.$emit('error', 'Failed to export translations');
      } finally {
        this.isExporting = false;
      }
    },
    startEdit(item) {
      this.editingItem = { ...item };
    },
    cancelEdit() {
      this.editingItem = null;
    },
    async saveEdit() {
      this.isSaving = true;
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          this.$emit('error', 'No authentication token found');
          return;
        }

        const response = await axios.post('/admin/localization4', {
          key: this.editingItem.key,
          language: this.selectedLanguage,
          text: this.editingItem.text
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data.status === 200) {
          const itemIndex = this.rawData.findIndex(
            item => item.key === this.editingItem.key && item.language === this.selectedLanguage
          );
          if (itemIndex !== -1) {
            this.rawData[itemIndex].text = this.editingItem.text;
          }
          this.editingItem = null;
          this.$emit('success', 'Translation saved successfully');
        }
      } catch (error) {
        console.error('Error saving translation:', error);
        this.$emit('error', 'Failed to save translation');
      } finally {
        this.isSaving = false;
      }
    }
  },
};
</script>

<style scoped>
.localization-container {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  padding: 1rem 2rem;
  padding-left: 200px;
  margin-left: 200px; /* Adjust based on your sidebar width */
}

.header-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 0;
}

.table-container {
  padding-right: 1rem;
}

.localization-selector {
  margin-right: 1rem;
  min-width: 120px;
}

.language-select {
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid #ddd;
  font-size: 1rem;
  width: 100%;
}

.language-select:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

.export-button {
  padding: 0.5rem 1rem;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.2s;
}

.export-button:hover {
  background-color: #45a049;
}

.export-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.translations-table {
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.translations-table th,
.translations-table td {
  padding: 0.75rem 1rem;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.translations-table th {
  background-color: #f8f9fa;
  font-weight: 600;
}

.translations-table tr:hover {
  background-color: #f8f9fa;
}

.edit-input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
}

.edit-input:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

.actions {
  white-space: nowrap;
}

.action-button {
  padding: 0.25rem 0.75rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  margin-right: 0.5rem;
  transition: background-color 0.2s;
}

.action-button.edit {
  background-color: #3b82f6;
  color: white;
}

.action-button.edit:hover {
  background-color: #2563eb;
}

.action-button.save {
  background-color: #4CAF50;
  color: white;
}

.action-button.save:hover {
  background-color: #45a049;
}

.action-button.save:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.action-button.cancel {
  background-color: #ef4444;
  color: white;
}

.action-button.cancel:hover {
  background-color: #dc2626;
}

/* Responsive table */
@media (max-width: 768px) {
  .header-controls {
    padding-left: 0;
  }
  
  .table-container {
    padding-left: 0;
  }
  
  .translations-table {
    font-size: 0.875rem;
  }
  
  .translations-table th,
  .translations-table td {
    padding: 0.5rem;
  }
  
  .header-controls {
    flex-direction: column;
    gap: 1rem;
  }
  
  .localization-selector {
    margin-right: 0;
    width: 100%;
  }
  
  .export-button {
    width: 100%;
  }
  
  .action-button {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
  }
}

.search-container {
  margin-bottom: 1rem;
}

.search-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

.search-input::placeholder {
  color: #999;
}
</style> 